
import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import donation1 from '../assets/donation1.jpg'
import donation2 from '../assets/donation2.jpg'
import donation3 from '../assets/donation3.jpg'
import donation4 from '../assets/donation4.jpg'
import donation5 from '../assets/donation5.jpg'
import donation6 from '../assets/donation6.jpg'

const ImageGallery = () => {
    const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Sample gallery images - replace with your actual images
  const galleryImages = [
    { id: 1, src: donation1, alt: "First Project" },
    { id: 2, src: donation2, alt: "Project 2" },
    { id: 3, src: donation3, alt: "Project 3" },
    { id: 4, src: donation4, alt: "Project 4" },
    { id: 5, src: donation5, alt: "Project 5" },
    { id: 6, src: donation6, alt: "Project 6" }
  ];

  const openModal = () => setShowModal(true);
  
  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const handleCloseFullImage = () => {
    setSelectedImage(null);
  };

  const navigateToImage = (direction) => {
    const newIndex = (currentIndex + direction + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  // Touch event handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const difference = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance
    
    if (difference > threshold) {
      // Swipe left, go to next image
      navigateToImage(1);
    } else if (difference < -threshold) {
      // Swipe right, go to previous image
      navigateToImage(-1);
    }
    
    // Reset values
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'ArrowRight') {
        navigateToImage(1);
      } else if (e.key === 'ArrowLeft') {
        navigateToImage(-1);
      } else if (e.key === 'Escape') {
        handleCloseFullImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  return (
    <div className="relative">
      {/* Projects button */}
      <button 
        onClick={openModal}
        className="h-9 px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-md shadow-sm hover:bg-purple-600"
      >
        Projects
      </button>

      {/* Gallery Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative bg-white rounded-lg shadow-xl w-11/12 md:w-3/4 lg:w-2/3 max-h-screen overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Project Gallery</h3>
              <button
                onClick={closeModal}
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal content */}
            <div className="p-6 overflow-y-auto max-h-96">
              {selectedImage ? (
                // Full-size image view with swipe functionality
                <div className="relative flex flex-col items-center"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <button
                    onClick={handleCloseFullImage}
                    className="absolute top-2 right-2 p-1 rounded-full bg-white bg-opacity-70 text-gray-800 hover:bg-opacity-100 z-10"
                  >
                    <X className="w-6 h-6" />
                  </button>
                  
                  {/* Navigation buttons */}
                  <button 
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-1 rounded-full hover:bg-opacity-70"
                    onClick={() => navigateToImage(-1)}
                  >
                    <ChevronLeft className="w-8 h-8 text-gray-800" />
                  </button>
                  
                  <button 
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-1 rounded-full hover:bg-opacity-70"
                    onClick={() => navigateToImage(1)}
                  >
                    <ChevronRight className="w-8 h-8 text-gray-800" />
                  </button>
                  
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="max-w-full max-h-80 object-contain"
                  />
                  <p className="mt-4 text-gray-700 font-medium">{selectedImage.alt}</p>
                  
                  <div className="flex justify-center mt-4 space-x-1">
                    {galleryImages.map((_, index) => (
                      <div 
                        key={index}
                        className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-purple-500' : 'bg-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                // Gallery grid
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {galleryImages.map((image, index) => (
                    <div 
                      key={image.id}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleImageClick(image, index)}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-40 object-cover rounded-md"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex justify-end p-4 border-t">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;